import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';

// Sentinel distinct de `null` : `null` = refresh en cours (non résolu), REFRESH_FAILED = refresh
// résolu par un échec. Sans cette distinction, les requêtes en attente (branche isRefreshing)
// filtrées sur `t !== null` ignoraient silencieusement un échec et restaient bloquées à vie.
const REFRESH_FAILED = '__REFRESH_FAILED__';

let isRefreshing = false;
const refreshDone$ = new BehaviorSubject<string | null>(null);

// Endpoints jamais soumis au cycle refresh-puis-retry : login/OTP (pas de session à rafraîchir),
// refresh lui-même (éviter la récursion), et logout — notamment appelé ci-dessous après un échec
// de refresh ; sans cette exemption, ce logout() (token déjà mort) prend lui aussi un 401, relance
// un refresh, échoue, rappelle logout()... boucle infinie visible dans Network.
const AUTH_BYPASS_PATTERNS = ['/auth/login', '/admin/auth/', '/auth/refresh', '/auth/logout'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  // Pas de Bearer sur /auth/refresh : le filtre JWT backend rejette (401) tout token expiré,
  // même sur un endpoint public — le refresh échouait donc dès que l'access token avait expiré.
  const token = req.url.includes('/auth/refresh') ? null : authService.accessToken;

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const isAuthEndpoint = AUTH_BYPASS_PATTERNS.some(pattern => req.url.includes(pattern));

      if (error.status !== 401 || isAuthEndpoint) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        return refreshDone$.pipe(
          filter(t => t !== null),
          take(1),
          switchMap(newToken =>
            newToken === REFRESH_FAILED
              ? throwError(() => error)
              : next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }))
          )
        );
      }

      isRefreshing = true;
      refreshDone$.next(null);

      return authService.refresh().pipe(
        switchMap(res => {
          isRefreshing = false;
          refreshDone$.next(res.accessToken);
          return next(req.clone({ setHeaders: { Authorization: `Bearer ${res.accessToken}` } }));
        }),
        catchError(refreshErr => {
          isRefreshing = false;
          refreshDone$.next(REFRESH_FAILED);
          authService.logout();
          return throwError(() => refreshErr);
        })
      );
    })
  );
};
